// src/hooks/useChats.js

import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AuthContext from '../context/AuthContext';

const useChats = (apiModels) => {
  const { id: chatIdFromUrl } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Новые состояния для пагинации
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Состояния для редактирования заголовка
  const [isEditingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Состояния для модального окна удаления чата
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);

  const fetchChats = useCallback(async (pageNum = 1, initialLoad = false) => {
    // Если это не первая загрузка и больше нет страниц, выходим
    if (!initialLoad && !hasNextPage) return;
    
    // Устанавливаем правильный флаг загрузки
    if (initialLoad) {
      setLoading(true);
    } else {
      setIsFetchingMore(true);
    }
    
    try {
      const res = await axios.get('/api/chat', { params: { page: pageNum, limit: 25 } });
      const { chats: newChats, hasNextPage: newHasNextPage } = res.data;
      
      setChats(prev => initialLoad ? newChats : [...prev, ...newChats]);
      setHasNextPage(newHasNextPage);
      setPage(pageNum);
      
      return newChats; // Возвращаем только что загруженные чаты
    } catch (err) {
      console.error('Ошибка при загрузке чатов:', err);
      setError('Не удалось загрузить историю чатов');
      toast.error('Не удалось загрузить историю чатов');
      return [];
    } finally {
      if (initialLoad) {
      setLoading(false);
      } else {
        setIsFetchingMore(false);
      }
    }
  }, [hasNextPage]);

  const fetchChat = useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      setLoading(true);
      const res = await axios.get(`/api/chat/${chatId}`);
      setCurrentChat(res.data);
      setNewTitle(res.data.title);
      localStorage.setItem('lastActiveChatId', chatId);
    } catch (err) {
      console.error(`Ошибка при загрузке чата ${chatId}:`, err);
      setError('Не удалось загрузить чат');
      toast.error('Не удалось загрузить чат');
      if (err.response && err.response.status === 404) {
        localStorage.removeItem('lastActiveChatId');
        navigate('/chat');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Эффект для инициализации и синхронизации с URL
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      // Загружаем первую страницу чатов
      const initialChats = await fetchChats(1, true);

      const queryParams = new URLSearchParams(location.search);
      const promptIdFromUrl = queryParams.get('prompt');
      const projectIdFromUrl = queryParams.get('project');

      if (chatIdFromUrl) {
        await fetchChat(chatIdFromUrl);
      } else if (promptIdFromUrl) {
        // Создаем новый чат с выбранным шаблоном
        try {
          const templateRes = await axios.get(`/api/prompts/${promptIdFromUrl}`);
          const template = templateRes.data;
          const newChat = await createNewChat({ 
            title: `Чат с шаблоном: ${template.title}`,
            navigateTo: false
          });
          if (newChat) {
            // Устанавливаем чат как текущий и перенаправляем
            navigate(`/chat/${newChat._id}?template=${promptIdFromUrl}`);
          }
        } catch (error) {
          console.error('Ошибка при создании чата с шаблоном:', error);
          // Fallback - создаем обычный чат
          const newChat = await createNewChat({ 
            title: 'Новый чат с шаблоном',
            navigateTo: false
          });
          if (newChat) {
            navigate(`/chat/${newChat._id}?template=${promptIdFromUrl}`);
          }
        }
      } else {
        const lastActiveChatId = localStorage.getItem('lastActiveChatId');
        // Проверяем, есть ли lastActiveChatId в уже загруженных чатах
        if (lastActiveChatId && initialChats.some(c => c._id === lastActiveChatId)) {
          navigate(`/chat/${lastActiveChatId}`);
        } else if (initialChats.length > 0) {
          const firstChat = initialChats[0];
          navigate(`/chat/${firstChat._id}`);
        } else {
          // Если чатов нет, новый будет создан при первом действии
          setCurrentChat(null);
        }
      }
      setLoading(false);
    };

    if(user) { // Запускаем только если пользователь авторизован
        initialize();
    }
  }, [chatIdFromUrl, user, navigate, location.search, fetchChats, fetchChat]);

  const createNewChat = useCallback(async (options = {}) => {
    const { projectId = null, title = 'Новый чат', navigateTo = true } = options;
    try {
      let apiModelId = '';
      if (apiModels && apiModels.length > 0) {
        apiModelId = apiModels[0]._id;
      }
      const res = await axios.post('/api/chat', { title, apiModelId, projectId });
      const newChat = res.data;
      setChats(prev => [newChat, ...prev]);
      setCurrentChat(newChat);
      localStorage.setItem('lastActiveChatId', newChat._id);
      if (navigateTo) {
        navigate(`/chat/${newChat._id}`);
      }
      toast.success('Новый чат создан');
      return newChat;
    } catch (err) {
      console.error('Ошибка при создании чата:', err.response?.data || err.message);
      setError(`Не удалось создать новый чат: ${err.response?.data?.message || err.message}`);
      toast.error('Не удалось создать новый чат');
      return null;
    }
  }, [apiModels, navigate]);

  const loadMoreChats = useCallback(() => {
    if (!isFetchingMore && hasNextPage) {
      fetchChats(page + 1);
    }
  }, [page, isFetchingMore, hasNextPage, fetchChats]);

  const openDeleteModal = (chatId) => {
    setChatToDelete(chatId);
    setDeleteModalOpen(true);
  };
  
  const confirmDeleteChat = async () => {
    if (!chatToDelete) return;
    try {
      await axios.delete(`/api/chat/${chatToDelete}`);
      const deletedChatId = chatToDelete;
      setChats(chats.filter(chat => chat._id !== deletedChatId));
      if (currentChat && currentChat._id === deletedChatId) {
        setCurrentChat(null);
        localStorage.removeItem('lastActiveChatId');
        navigate('/chat');
      }
      toast.success('Чат удален');
    } catch (err) {
      setError('Не удалось удалить чат');
      toast.error('Не удалось удалить чат');
    } finally {
      setDeleteModalOpen(false);
      setChatToDelete(null);
    }
  };

  const cancelDeleteChat = () => {
    setDeleteModalOpen(false);
    setChatToDelete(null);
  };

  const updateChatTitle = async () => {
    if (!newTitle.trim() || !currentChat) {
      setEditingTitle(false);
      return;
    }
    try {
      const res = await axios.put(`/api/chat/${currentChat._id}`, { title: newTitle });
      const updatedChat = res.data;
      setCurrentChat(updatedChat);
      setChats(chats.map(chat => chat._id === currentChat._id ? updatedChat : chat));
      setEditingTitle(false);
    } catch (err) {
      setError('Не удалось обновить название чата');
      toast.error('Не удалось обновить название чата');
    }
  };
  
  const moveChatToProject = async (chatId, projectId) => {
    try {
      const chat = chats.find(c => c._id === chatId);
      if (!chat) return;
      const res = await axios.put(`/api/chat/${chatId}`, { projectId: projectId });
      const updatedChat = res.data;

      setChats(prev => prev.map(c => c._id === chatId ? updatedChat : c));
      
      if (currentChat && currentChat._id === chatId) {
        setCurrentChat(updatedChat);
      }
      
      toast.success(projectId ? 'Чат перемещен в проект' : 'Чат перемещен в "Без проекта"');
    } catch (err) {
      console.error('Ошибка при перемещении чата:', err);
      toast.error('Не удалось переместить чат');
    }
  };
  
  // Функция для обновления чата извне (например, после получения сообщения)
  const updateCurrentChat = (updatedChat) => {
    setCurrentChat(updatedChat);
    setChats(prevChats => 
      prevChats.map(chat => 
        chat._id === updatedChat._id ? updatedChat : chat
      )
    );
  };

  return {
    chats,
    currentChat,
    loading,
    error,
    isFetchingMore,
    hasNextPage,
    loadMoreChats,
    fetchChats, // может понадобиться для обновления после удаления проекта
    
    actions: {
      createNewChat,
      selectChat: (chatId) => navigate(`/chat/${chatId}`),
      openDeleteModal,
      moveChatToProject,
      updateCurrentChat, // Важно для связи с другими хуками
    },

    titleEditor: {
      isEditing: isEditingTitle,
      startEditing: () => {
          if(currentChat) setNewTitle(currentChat.title);
          setEditingTitle(true);
      },
      cancelEditing: () => setEditingTitle(false),
      submit: updateChatTitle,
      value: newTitle,
      setValue: setNewTitle,
    },

    deleteModal: {
      isOpen: isDeleteModalOpen,
      close: cancelDeleteChat,
      confirm: confirmDeleteChat,
    }
  };
};

export default useChats;